import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { EventType } from '../../types/ViewModel/EventType';
import { EventTypeGroup } from '../../types/ViewModel/EventTypeGroup';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import { InputDataTypeEnum } from '../../enums/inputDataTypeEnum';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { getParentPortCallFromNode, getParentBerthCallFromNode } from '../rotationUtils';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import { PortCall } from '../../types/ViewModel/PortCall';
import { ErrorMsg } from '../../types/ViewModel/ErrorMsg';
import eventTypeGroupChangeLogUtils from './eventTypeGroupChangeLogUtils';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { compareActualEventDateTime } from './eventDateUtils';
import { resolveEventTypeName } from './eventTypeNameOverrides';

export default () => {
    const { isMultiple, isConcurrent, getRequiredPreviousTypeId } = eventTypeGroupChangeLogUtils();

    const createErrMsg = (ev1: TankersEvent, ev2: TankersEvent, notValEv: TankersEvent, phrase: string): ErrorMsg => {
        return new ErrorMsg(true, phrase, false, notValEv);
    };

    const getVoyageNameOrSea = (ev: TankersEvent, portCall: PortCall | null): string => {
        if (portCall) {
            if (ev.eventTypeContextId === ReportLocationEnum.Sea) return ' at sea';
            if (portCall) return " in port call '" + portCall.voyageNumberPresentationString + ' ' + portCall.name + "'";
        }
        return '';
    };

    // Generate new unique guid
    const generateUuid = (): string => crypto.randomUUID();

    const getMovedDeletedStylingClass = (type: RotationTreeEnum, isDeleted: boolean): string | null => {
        switch (type) {
            case RotationTreeEnum.Sea:
            case RotationTreeEnum.PortCall:
                if (isDeleted) {
                    return 'port-call-deleted';
                } else {
                    return 'port-call-moved';
                }
            case RotationTreeEnum.BerthCall:
                if (isDeleted) {
                    return 'berth-call-deleted';
                } else {
                    return 'berth-call-moved';
                }
            case RotationTreeEnum.CargoOperation:
                if (isDeleted) {
                    return 'cargo-operation-deleted';
                } else {
                    return 'cargo-operation-moved';
                }
        }
    };

    const getFirstPortCallDate = (portCallEvList: TankersEvent[]): Date | null => {
        const portCallEvents: TankersEvent[] = portCallEvList.sort((a, b) => compareActualEventDateTime(a, b, false));
        if (portCallEvents && portCallEvents.length > 0) {
            return portCallEvents[0].eventDate.actualDateTimeUtc;
        }
        return null;
    };

    const getParentEventsSameSequence = (eventArray: TankersEvent[], checkEventType: EventType, eventTypeGroupId: number): TankersEvent[] => {
        let evInParentGroupArray: TankersEvent[] = [];
        if (checkEventType.eventTypeGroupSequenceDependencyIdentity) {
            evInParentGroupArray = eventArray.filter((ev: TankersEvent) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroupId === eventTypeGroupId &&
                    checkEventType.eventTypeGroupSequenceDependencyIdentity === ev.eventTypeGroupSequenceIdentity
                );
            });
        } else if (checkEventType.parentSequenceNumber) {
            evInParentGroupArray = eventArray.filter((ev: TankersEvent) => {
                return (
                    ev.eventType &&
                    ev.eventType.eventTypeGroupId === eventTypeGroupId &&
                    checkEventType.parentSequenceNumber === ev.concurrentSequenceNumber
                );
            });
        } else {
            evInParentGroupArray = eventArray.filter((ev: TankersEvent) => {
                return ev.eventType && ev.eventType.eventTypeGroupId === eventTypeGroupId;
            });
        }
        return evInParentGroupArray;
    };

    const isRequiredEventTypeInSameEventTypeGroup = (evTypeGroup: EventTypeGroup, node: IRotationNode): boolean => {
        const reqPrevTypeId = getRequiredPreviousTypeId(evTypeGroup, node.firstTimeInPortCall);
        if (reqPrevTypeId && evTypeGroup.eventTypeArray) {
            const evType = evTypeGroup.eventTypeArray.find((et) => et.eventTypeId === reqPrevTypeId);
            if (evType) {
                return true;
            }
        }
        return false;
    };

    // Finds the event type group in the event type level hierarchy
    // const getEventTypeGroup(eventTypeGroupId: number, eventTypeData: EventTypeData): EventTypeGroup | null {
    //   if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
    //     var grArray = eventTypeData.eventTypeLevelArray.flatMap(o => o.eventTypeGroupArray);
    //     var grItem =  grArray.find(o => o.eventTypeGroupId === eventTypeGroupId);
    //     return (grItem ?? null);
    //   }
    //   return null;
    // }

    const hasInputGroups = (evType: EventType | null): boolean => {
        return !!(evType && evType.eventTypeInputGroupArray && evType.eventTypeInputGroupArray.length > 0);
    };

    // Check if all event sequence groups are finished
    const hasNotAfterOk = (eventArray: TankersEvent[], checkEvent: TankersEvent, selectedNode: IRotationNode): boolean => {
        // Only for multiple groups
        if (
            checkEvent.eventType &&
            checkEvent.eventType.eventTypeGroup &&
            isMultiple(checkEvent.eventType.eventTypeGroup, selectedNode.firstTimeInPortCall)
        ) {
            // Find all events for actual group
            const eventListForGroup: TankersEvent[] = eventArray.filter(
                (ev) => ev.eventType && checkEvent.eventType && ev.eventType.eventTypeGroupId === checkEvent.eventType.eventTypeGroupId
            );
            if (eventListForGroup && eventListForGroup.length > 0) {
                const ind = findMissingEventTypeInSequence(eventListForGroup);
                if (ind !== -1) {
                    // Event type in not after group not finished
                    return true;
                }
            }
        }
        return false;
    };

    // Find the last event for an event group
    const findIndexOfLastEventType = (evArrayInput: TankersEvent[]): number => {
        let currentIndex = 0;
        let indexOfLast = evArrayInput.findIndex((ev) => ev.eventType && !ev.eventType.nextEventTypeInGroupId);
        if (indexOfLast === -1) {
            evArrayInput.forEach((evItem) => {
                const evInd = evArrayInput.findIndex((ev) => {
                    return evItem.eventType && evItem.eventTypeId !== ev.eventTypeId && ev.eventTypeId === evItem.eventType.nextEventTypeInGroupId;
                });
                if (evInd === -1) {
                    indexOfLast = currentIndex;
                }
                currentIndex++;
            });
        }
        return indexOfLast;
    };

    // Sort ordered events, by starting at the last, which has event type with NextEventTypeInGroupId = null, and go backwards until the first
    // Returns the array of events starting with the first element
    const sortOrderedEvents = (evArrayInput: TankersEvent[]): TankersEvent[] => {
        const evArray: TankersEvent[] = [];
        let exit = false;
        const arrLen: number = evArrayInput.length;
        let arrCnt = 0;
        let nextId: number | null;

        if (evArrayInput && evArrayInput.length > 0) {
            // Find last event type
            const lastEventIndex = findIndexOfLastEventType(evArrayInput);
            if (lastEventIndex !== -1) {
                evArray.push(evArrayInput[lastEventIndex]);
                nextId = evArrayInput[lastEventIndex].eventTypeId;
                while (arrCnt < arrLen - 1 && !exit) {
                    const previousEventTypeGroup = evArrayInput.find((ev) => {
                        return ev.eventType && ev.eventType.nextEventTypeInGroupId === nextId;
                    });
                    if (previousEventTypeGroup) {
                        evArray.unshift(previousEventTypeGroup);
                        nextId = previousEventTypeGroup.eventTypeId;
                    } else {
                        exit = true;
                    }
                    arrCnt++;
                }
            }
        }
        return evArray;
    };

    // Check if event is dependent on other event type group
    const hasEventGroupDependency = (checkEvent: TankersEvent, dependentToEvent: TankersEvent): boolean => {
        if (!checkEvent.eventTypeGroupSequenceDependencyIdentity) {
            return false;
        }
        return checkEvent.eventTypeGroupSequenceDependencyIdentity === dependentToEvent.eventTypeGroupSequenceIdentity;
    };

    const filterDuplicateEvents = (evArr: TankersEvent[]): TankersEvent[] => {
        const resultEvArr: TankersEvent[] = [];

        evArr.forEach((ev) => {
            const evExist = resultEvArr.find((o) => o.eventTypeId === ev.eventTypeId);
            if (!evExist) {
                resultEvArr.push(ev);
            }
        });
        return resultEvArr;
    };

    // Finds the event in event type group with missing next event type, for multiple event type groups
    // If event with missing next event type exists, return the index, else return -1
    const findMissingEventTypeInSequence = (eventArrayInGroup: TankersEvent[]): number => {
        let foundIndex = -1;

        if (eventArrayInGroup && eventArrayInGroup.length > 0) {
            // const seqIdArray: string[] = _.uniq(_.pluck(eventArrayInGroup, 'eventTypeGroupSequenceIdentity'));
            let seqIdArray = eventArrayInGroup.map((eti) => eti && eti.eventTypeGroupSequenceIdentity);
            seqIdArray = seqIdArray.filter(function (v, i) {
                return seqIdArray.indexOf(v) == i;
            });
            let keepGoing = true;
            let seqInd = 0;

            while (keepGoing) {
                // Find all items in sequence
                const seqArray = eventArrayInGroup.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[seqInd]);

                const allGroupTypes = seqArray[0]?.eventType?.eventTypeGroup?.eventTypeArray;
                const sortedAllTypes = allGroupTypes && allGroupTypes.length > 0 ? sortOrderedEventTypes(allGroupTypes) : null;
                const sortedGroupTypes = sortedAllTypes ? sortedAllTypes.filter((et) => !et.deleted) : null;

                if (sortedGroupTypes && sortedGroupTypes.length > 0) {
                    const missingType = sortedGroupTypes.find((et) => !seqArray.some((ev) => ev.eventTypeId === et.eventTypeId));
                    if (missingType) {
                        const missingIndex = sortedGroupTypes.findIndex((et) => et.eventTypeId === missingType.eventTypeId);
                        let refEvent: TankersEvent | undefined;
                        for (let i = missingIndex - 1; i >= 0; i--) {
                            refEvent = seqArray.find((ev) => ev.eventTypeId === sortedGroupTypes[i].eventTypeId);
                            if (refEvent) break;
                        }
                        if (!refEvent) {
                            for (let i = missingIndex + 1; i < sortedGroupTypes.length; i++) {
                                refEvent = seqArray.find((ev) => ev.eventTypeId === sortedGroupTypes[i].eventTypeId);
                                if (refEvent) break;
                            }
                        }
                        foundIndex = eventArrayInGroup.findIndex((ev) => ev.eventId === (refEvent?.eventId ?? seqArray[0]?.eventId));
                        keepGoing = false;
                    } else {
                        seqInd++;
                        keepGoing = seqInd < seqIdArray.length;
                    }
                } else {
                    const missingNextEvent = seqArray.find(
                        (ev) =>
                            ev.eventType?.nextEventTypeInGroupId &&
                            !seqArray.some((nextEv) => nextEv.eventTypeId === ev.eventType!.nextEventTypeInGroupId)
                    );
                    if (missingNextEvent) {
                        foundIndex = eventArrayInGroup.findIndex((ev) => ev.eventId === missingNextEvent.eventId);
                        keepGoing = false;
                    } else {
                        const lastItemInSeq = seqArray.find((ev) => ev.eventType && !ev.eventType.nextEventTypeInGroupId);
                        if (!lastItemInSeq) {
                            foundIndex = eventArrayInGroup.findIndex((ev) => ev.eventId === seqArray[seqArray.length - 1].eventId);
                            keepGoing = false;
                        } else {
                            seqInd++;
                            keepGoing = seqInd < seqIdArray.length;
                        }
                    }
                }
            }
        }
        return foundIndex;
    };

    const isNull = (nr: number | null): boolean => {
        return !nr;
    };

    // Checks if the event has the correct level and entity
    const entityOk = (event: TankersEvent, selectedNode: IRotationNode, includeChildren: boolean): boolean => {
        // AB#39967: soft-deleted (Deleted=1) rows must never be treated as active events —
        // they leak through consumption paths (e.g. next-available-event-type/sequence logic)
        // and can block legitimate event registration (e.g. BOSP) or raise false warnings.
        if (event.deleted) {
            return false;
        }
        switch (selectedNode.type) {
            case RotationTreeEnum.Sea:
                return !isNull(event.portCallId) && event.portCallId === selectedNode.id && (isNull(event.berthCallId) || includeChildren);
            //return (!isNull(event.portCallId) && (event.portCallId === selectedNode.id) && (event.eventTypeContextId === ReportLocationEnum.Sea) && (isNull(event.berthCallId) || includeChildren));
            case RotationTreeEnum.PortCall:
                return !isNull(event.portCallId) && event.portCallId === selectedNode.id && (isNull(event.berthCallId) || includeChildren);
            //return (!isNull(event.portCallId) && (event.portCallId === selectedNode.id) && (event.eventTypeContextId === ReportLocationEnum.Port) && (isNull(event.berthCallId) || includeChildren));
            case RotationTreeEnum.BerthCall:
                return !isNull(event.berthCallId) && event.berthCallId === selectedNode.id && (isNull(event.cargoOperationId) || includeChildren);
            case RotationTreeEnum.CargoOperation:
                return !isNull(event.cargoOperationId) && event.cargoOperationId === selectedNode.id;
            default:
                return false;
        }
    };

    // Checks if the event is on the selected node
    const isEventOnSelectedNode = (verifyEvent: TankersEvent, event: TankersEvent): boolean => {
        switch (verifyEvent.getEventLevel()) {
            case RotationTreeEnum.Sea:
            case RotationTreeEnum.PortCall:
                return event.portCallId === verifyEvent.portCallId;
            case RotationTreeEnum.BerthCall:
                if (event.berthCallId) {
                    return (
                        !isNull(verifyEvent.berthCallId) &&
                        event.berthCallId === verifyEvent.berthCallId &&
                        event.portCallId === verifyEvent.portCallId
                    );
                } else {
                    return true;
                }
            case RotationTreeEnum.CargoOperation:
                if (event.cargoOperationId) {
                    return (
                        !isNull(verifyEvent.cargoOperationId) &&
                        event.cargoOperationId === verifyEvent.cargoOperationId &&
                        event.berthCallId === verifyEvent.berthCallId &&
                        event.portCallId === verifyEvent.portCallId
                    );
                } else {
                    return true;
                }
            default:
                return false;
        }
    };

    // Find events for entity, parent and grandparents (if applicable) and children (if applicable)
    const getParentEvents = (eventArray: TankersEvent[], selectedNode: IRotationNode): TankersEvent[] => {
        let evList: TankersEvent[] = [];

        switch (selectedNode.type) {
            case RotationTreeEnum.Sea:
                evList = eventArray?.filter((ev) => {
                    return entityOk(ev, selectedNode, true);
                });
                break;
            case RotationTreeEnum.PortCall:
                evList = eventArray?.filter((ev) => {
                    return entityOk(ev, selectedNode, true);
                });
                break;
            case RotationTreeEnum.BerthCall: {
                const portCall = getParentPortCallFromNode(selectedNode);
                if (portCall) {
                    // Find berth events for this berth
                    evList = eventArray?.filter((ev) => {
                        return ev.portCallId === portCall.id && ev.berthCallId && entityOk(ev, selectedNode, true);
                    });
                    // Find port events for parent port
                    const evList2 = eventArray?.filter((ev) => {
                        return ev.portCallId && ev.portCallId === portCall.id && !ev.berthCallId;
                    });
                    evList = evList.concat(evList2);
                }
                break;
            }
            case RotationTreeEnum.CargoOperation: {
                const berthCall = getParentBerthCallFromNode(selectedNode);
                if (berthCall) {
                    const portCall2 = getParentPortCallFromNode(berthCall);
                    if (portCall2) {
                        // Find cargo operation events for this cargo op
                        evList = eventArray.filter((ev) => {
                            return (
                                ev.portCallId === portCall2.portCallId && ev.berthCallId === berthCall.berthCallId && entityOk(ev, selectedNode, true)
                            );
                        });
                        // Find berth events for parent berth
                        const evList2 = eventArray.filter((ev) => {
                            return ev.portCallId === portCall2.id && ev.berthCallId && ev.berthCallId === berthCall.id && !ev.cargoOperationId;
                        });
                        evList = evList.concat(evList2);
                        // Find port events for grandparent port
                        const evList3 = eventArray.filter((ev) => {
                            return ev.portCallId && ev.portCallId === portCall2.id && !ev.berthCallId;
                        });
                        evList = evList.concat(evList3);
                    }
                }
                break;
            }
        }
        return evList;
    };

    const findParentEventType = (eventTypeId: number | null, eventTypeData: EventTypeData): EventType | null => {
        let parentEventType: EventType | null = null;
        if (eventTypeId && eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            eventTypeData.eventTypeLevelArray.forEach((evTypeLevelItem) => {
                evTypeLevelItem.eventTypeGroupArray.forEach((evTypeGroupItem) => {
                    if (!parentEventType) {
                        const parent = evTypeGroupItem.eventTypeArray?.find((evType) => evType.eventTypeId === eventTypeId);
                        if (parent) {
                            parentEventType = parent;
                        }
                    }
                });
            });
        }
        return parentEventType;
    };

    const hasConcurrentParentInCompleteTree = (
        initialEventType: EventType | null,
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): EventType | null => {
        let concurrentEventType: EventType | null = null;
        if (initialEventType && initialEventType.eventTypeGroup) {
            let checkEventType: EventType | null = findParentEventType(
                getRequiredPreviousTypeId(initialEventType.eventTypeGroup, node.firstTimeInPortCall),
                eventTypeData
            );
            let keepGoing = !!checkEventType;

            while (keepGoing) {
                if (checkEventType && checkEventType.eventTypeGroup && checkEventType.levelId === initialEventType.levelId) {
                    if (isConcurrent(checkEventType.eventTypeGroup, node.firstTimeInPortCall)) {
                        concurrentEventType = checkEventType;
                        keepGoing = false;
                    } else {
                        checkEventType = findParentEventType(
                            getRequiredPreviousTypeId(checkEventType.eventTypeGroup, node.firstTimeInPortCall),
                            eventTypeData
                        );
                    }
                } else {
                    keepGoing = false;
                }
            }
        }
        return concurrentEventType;
    };

    const getConcurrentSeqNumberInCompleteTree = (
        initialEvSeq: TankersEvent[],
        eventType: EventType,
        event: TankersEvent,
        evArray: TankersEvent[],
        node: IRotationNode
    ): number | null => {
        let concurrentSeqNumber: number | null = null;
        let keepGoing = true;
        let checkEvSeq: TankersEvent[] = initialEvSeq;
        let checkEvType: EventType = eventType;
        let checkEvent = event;

        if (checkEvSeq && checkEvSeq.length > 0) {
            while (keepGoing) {
                if (
                    checkEvSeq[0].eventType &&
                    checkEvSeq[0].eventType.eventTypeGroup &&
                    isConcurrent(checkEvSeq[0].eventType.eventTypeGroup, node.firstTimeInPortCall)
                ) {
                    concurrentSeqNumber = checkEvSeq[0].concurrentSequenceNumber;
                    keepGoing = false;
                } else {
                    if (checkEvent) {
                        checkEvSeq = evArray.filter(
                            (ev) =>
                                ev.eventTypeGroupSequenceIdentity === checkEvent.eventTypeGroupSequenceDependencyIdentity &&
                                ev.eventType &&
                                ev.eventType.levelId === eventType.levelId
                        );
                    } else {
                        checkEvSeq = evArray.filter(
                            (ev) =>
                                checkEvType.eventTypeGroup &&
                                ev.eventTypeId === getRequiredPreviousTypeId(checkEvType.eventTypeGroup, node.firstTimeInPortCall) &&
                                ev.concurrentSequenceNumber === checkEvType.parentSequenceNumber &&
                                ev.eventType &&
                                ev.eventType.levelId === eventType.levelId
                        );
                    }

                    if (!checkEvSeq || checkEvSeq.length === 0 || !checkEvSeq[0].eventType) {
                        keepGoing = false;
                    } else {
                        checkEvType = checkEvSeq[0].eventType;
                        checkEvent = checkEvSeq[0];
                    }
                }
            }
        }
        return concurrentSeqNumber;
    };

    const getParentEvSeqForParentSeqNumber = (
        parentSeqNumber: number,
        parentGroupEvSeq: TankersEvent[],
        eventType: EventType,
        evArray: TankersEvent[],
        node: IRotationNode
    ): TankersEvent[] => {
        let keepGoingSeq = true;
        let parentEvSeq: TankersEvent[] = [];

        if (parentGroupEvSeq && parentGroupEvSeq.length > 0) {
            // const seqIdArray: string[] = _.uniq(_.pluck(parentGroupEvSeq, 'eventTypeGroupSequenceIdentity'));
            let seqIdArray = parentGroupEvSeq.map((eti) => eti && eti.eventTypeGroupSequenceIdentity);
            seqIdArray = seqIdArray.filter(function (v, i) {
                return seqIdArray.indexOf(v) == i;
            });

            let seqCounter = 0;

            while (keepGoingSeq) {
                const seqArray = parentGroupEvSeq.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[seqCounter]);
                if (seqArray && seqArray.length > 0) {
                    if (parentSeqNumber === getConcurrentSeqNumberInCompleteTree(seqArray, eventType, seqArray[0], evArray, node)) {
                        keepGoingSeq = false;
                        parentEvSeq = seqArray;
                    } else {
                        seqCounter++;
                        keepGoingSeq = seqCounter < seqIdArray.length;
                    }
                } else {
                    keepGoingSeq = false;
                }
            }
        }
        return parentEvSeq;
    };

    const getEventTypeDisplayNameForConcurrent = (
        initialEvSeq: TankersEvent[],
        eventType: EventType | null,
        event: TankersEvent | null,
        evArray: TankersEvent[],
        seqCounter: number | null,
        node: IRotationNode
    ): string | null => {
        let displayName: string | null = null;
        let keepGoing = true;
        let checkEvSeq: TankersEvent[] = initialEvSeq;
        let seqId: string | null = null;
        let checkEvType: EventType | null = eventType;
        let seqNumber: number | null = seqCounter;
        let checkEvent = event;
        let hasStarted = false;

        if (initialEvSeq && initialEvSeq.length > 0) {
            seqId = initialEvSeq[0].eventTypeGroupSequenceIdentity;
        }

        while (keepGoing) {
            if ((checkEvType && checkEvType.eventTypeGroup && isConcurrent(checkEvType.eventTypeGroup, node.firstTimeInPortCall)) || hasStarted) {
                const parentDisplayName = getEventTypeInputDisplayStr(evArray, checkEvType, seqId, false, true, seqNumber, node);
                displayName = displayName ? parentDisplayName + ' ' + displayName : parentDisplayName;
                hasStarted = true;
            }

            if (checkEvent) {
                checkEvSeq = evArray.filter(
                    (ev) =>
                        ev.eventTypeGroupSequenceIdentity === checkEvent?.eventTypeGroupSequenceDependencyIdentity &&
                        ev.eventType &&
                        eventType &&
                        ev.eventType.levelId === eventType.levelId
                );
            } else {
                checkEvSeq = evArray.filter(
                    (ev) =>
                        checkEvType &&
                        ev.eventTypeGroupSequenceIdentity === checkEvType.eventTypeGroupSequenceDependencyIdentity &&
                        ev.eventType &&
                        eventType &&
                        ev.eventType.levelId === eventType.levelId
                );
            }

            if (!checkEvSeq || checkEvSeq.length === 0 || !checkEvSeq[0].eventType) {
                keepGoing = false;
            } else {
                seqId = checkEvSeq[0].eventTypeGroupSequenceIdentity;
                checkEvType = checkEvSeq[0].eventType;
                seqNumber = checkEvSeq[0].concurrentSequenceNumber;
                checkEvent = checkEvSeq[0];
                if (checkEvType.eventTypeGroup && !isConcurrent(checkEvType.eventTypeGroup, node.firstTimeInPortCall) && hasStarted) {
                    keepGoing = false;
                }
            }
        }
        return displayName;
    };

    // Finds the sequence identity for another event type group which the current event is dependent on
    // Current event need to have requiredPreviousTypeId set to the events within the same event type group,
    // or be dependent on conditional mandatory event type groups
    const findEventTypeGroupSequenceDependentIdentity = (
        eventArray: TankersEvent[],
        eventTypeData: EventTypeData,
        selectedNode: IRotationNode,
        checkEvent: TankersEvent
    ): string | null => {
        let sequenceIdentity: string | null = null;
        eventArray = getParentEvents(eventArray, selectedNode);
        let requiredPreviousEvent: TankersEvent | null = null;

        // For a new event, not update
        if (checkEvent && checkEvent.eventType && checkEvent.eventType.eventTypeGroupSequenceDependencyIdentity) {
            return checkEvent.eventType.eventTypeGroupSequenceDependencyIdentity;
        }

        // Check if event has required previous event type
        const reqPrevTypeId = getRequiredPreviousTypeId(checkEvent.eventType?.eventTypeGroup, selectedNode.firstTimeInPortCall);
        if (checkEvent.eventType && checkEvent.eventType.eventTypeGroup && reqPrevTypeId) {
            // Find event of required event type
            const req = eventArray.find((ev) => {
                return checkEvent.eventType && checkEvent.eventType.eventTypeGroup && ev.eventTypeId === reqPrevTypeId;
            });
            if (req) {
                requiredPreviousEvent = req;
            } else {
                requiredPreviousEvent = null;
            }
        } else if (
            checkEvent.eventType &&
            checkEvent.eventType.eventTypeGroup &&
            checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray
        ) {
            checkEvent.eventType.eventTypeGroup.requiredConditionalMandatoryArray.forEach((rcm) => {
                const evReq = eventArray.find((ev) => ev.eventTypeId === rcm.requiredPreviousTypeId);
                if (evReq) {
                    requiredPreviousEvent = evReq;
                }
            });
        }
        if (requiredPreviousEvent) {
            // Find all events of required event type group
            let eventListForParentGroup: TankersEvent[] = eventArray.filter((ev) => {
                return (
                    ev.eventType &&
                    requiredPreviousEvent &&
                    requiredPreviousEvent.eventType &&
                    requiredPreviousEvent.eventType.eventTypeGroupId &&
                    ev.eventType.eventTypeGroupId === requiredPreviousEvent.eventType.eventTypeGroupId
                );
            });
            if (eventListForParentGroup && eventListForParentGroup.length > 0) {
                if (
                    hasConcurrentParentInCompleteTree(checkEvent.eventType, eventTypeData, selectedNode) &&
                    checkEvent.eventType?.parentSequenceNumber
                ) {
                    eventListForParentGroup = eventListForParentGroup.filter(
                        (ev) => ev.concurrentSequenceNumber === checkEvent.eventType?.parentSequenceNumber
                    );
                    // if (parentEv) {
                    //   sequenceIdentity = parentEv.eventTypeGroupSequenceIdentity;
                    // }
                }
                if (!sequenceIdentity && !checkEvent.eventTypeGroupSequenceDependencyIdentity) {
                    // Find the event type group with missing event types, if exists
                    const ind = findMissingEventTypeInSequence(eventListForParentGroup);
                    if (ind !== -1) {
                        sequenceIdentity = eventListForParentGroup[ind].eventTypeGroupSequenceIdentity;
                    } else {
                        // Find latest event for completed parent event sequences
                        const evParentSorted = eventListForParentGroup.sort((a, b) => compareActualEventDateTime(a, b, false));
                        const reqEvInSequence = eventListForParentGroup.find(
                            (ev) =>
                                ev.eventTypeGroupSequenceIdentity === evParentSorted[evParentSorted.length - 1].eventTypeGroupSequenceIdentity &&
                                requiredPreviousEvent &&
                                requiredPreviousEvent.eventType &&
                                ev.eventTypeId === requiredPreviousEvent.eventType.eventTypeId
                        );
                        if (reqEvInSequence) {
                            sequenceIdentity = reqEvInSequence.eventTypeGroupSequenceIdentity;
                        }
                    }
                }
            }
        }
        if (sequenceIdentity) {
            return sequenceIdentity;
        }
        return checkEvent.eventTypeGroupSequenceDependencyIdentity;
    };

    // Generates description based on event input values
    const getEventInputDescription = (event: TankersEvent): string => {
        let desc = '';

        if (event.eventTypeInputRegList) {
            const inpDropdownList = event.eventTypeInputRegList.filter((inpReg) => {
                return inpReg.inputDataTypeId === InputDataTypeEnum.Dropdown;
            });
            if (inpDropdownList && inpDropdownList.length > 0) {
                let displayStr = '';
                let first = true;
                if (inpDropdownList[0].dropdownGroupReg) {
                    inpDropdownList[0].dropdownGroupReg.dropdownItemRegList.map((dir) => {
                        if (!first) {
                            displayStr += '/' + dir.dropdownItemValue;
                        } else {
                            displayStr += dir.dropdownItemValue;
                            first = false;
                        }
                    });
                    desc = displayStr;
                }
            } else {
                const inpStrList = event.eventTypeInputRegList.filter((inpReg) => {
                    return inpReg.inputDataTypeId === InputDataTypeEnum.String;
                });
                if (inpStrList && inpStrList.length > 0) {
                    desc = inpStrList[0].stringValueReg;
                }
            }
        }
        return desc;
    };

    const getDepSeqIdentity = (evType: EventType): string | null => {
        return evType.eventTypeGroupSequenceDependencyIdentity;
    };

    const generateEventNameWithParentDependency = (ev: TankersEvent, evParent: TankersEvent): string => {
        if (ev.eventType) {
            const eventTypeName = resolveEventTypeName(ev.eventType, ev);
            return evParent.dependencyDisplayName ? eventTypeName + ' ' + evParent.dependencyDisplayName : eventTypeName;
        }
        return '';
    };

    const generateEventNameWithDependencyAndDate = (ev: TankersEvent): string => {
        if (ev.eventType) {
            const eventTypeName = resolveEventTypeName(ev.eventType, ev);
            return (
                "'" +
                (ev.dependencyDisplayName ? eventTypeName + ' ' + ev.dependencyDisplayName : eventTypeName) +
                "' " +
                prettifyDateTimeHHmm(ev.eventDate.actualDateTimeLocal)
            );
        }
        return '';
    };

    const generateEventNameWithDependency = (ev: TankersEvent): string => {
        if (ev.eventType) {
            const eventTypeName = resolveEventTypeName(ev.eventType, ev);
            return "'" + (ev.dependencyDisplayName ? eventTypeName + ' ' + ev.dependencyDisplayName : eventTypeName) + "'";
        }
        return '';
    };

    const generateNewEventNameWithDependency = (ev: TankersEvent): string => {
        if (ev.eventType && ev.eventType.displayNameToolbox) {
            return "'" + ev.eventType.displayNameToolbox + "'";
        }
        return generateEventNameWithDependency(ev);
    };

    const generateDisplayName = (dispName: string | null): string => {
        if (!dispName) {
            return "'";
        }
        return ' ' + dispName + "'";
    };

    const getEventTypeName = (evType: EventType): string => {
        return "'" + evType.name + "'";
    };

    const generateEventNameWithParent = (
        eventType: EventType,
        parentEventType: EventType,
        parentName: string | null,
        currentName: string | null
    ): string | null => {
        let dispName: string | null = null;
        if (parentName) {
            dispName = '(' + parentEventType.shortName + ' ' + parentName + ')';
        }
        if (currentName && eventType.eventTypeGroup && eventType.eventTypeGroup.generateConcurrentId) {
            dispName += dispName ? ' (' + eventType.shortName + ' ' + currentName + ')' : '';
        }
        return dispName;
    };

    const getConcurrentParent = (eventArray: TankersEvent[], eventTypeGroup: EventTypeGroup | null, node: IRotationNode): EventType | null => {
        if (eventTypeGroup) {
            const reqPrevTypeId = getRequiredPreviousTypeId(eventTypeGroup, node.firstTimeInPortCall);
            if (reqPrevTypeId) {
                const parentEvent = eventArray.find((ev) => ev.eventTypeId === reqPrevTypeId);

                // Check if parent exist, and is concurrent
                if (
                    parentEvent &&
                    parentEvent.eventType &&
                    parentEvent.eventType.eventTypeGroup &&
                    isConcurrent(parentEvent.eventType.eventTypeGroup, node.firstTimeInPortCall)
                ) {
                    return parentEvent.eventType;
                }
            }
        }
        return null;
    };

    const generateDependencyDisplayName = (
        eventArray: TankersEvent[],
        seqArray: TankersEvent[],
        event: TankersEvent | null,
        eventType: EventType | null,
        dropdownValuesExist: boolean,
        seqCounter: number,
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): string | null => {
        if (event && event.dependencyDisplayName) {
            return event.dependencyDisplayName;
        }
        const evTypeGroupInput: EventTypeGroup | null = eventType ? eventType.eventTypeGroup : null;
        let seqNumber: number | null = 0;
        if (seqArray && seqArray.length > 0) {
            seqNumber = seqArray[0].concurrentSequenceNumber;
        } else {
            seqNumber = seqCounter;
        }

        let dispName: string | null = null;
        if (eventType && eventType.concurrentEventTypeInputId) {
            dispName = getEventTypeDisplayName(seqArray, eventType, dropdownValuesExist);
            if (!dispName) {
                if (evTypeGroupInput && evTypeGroupInput.generateConcurrentId && !dropdownValuesExist && seqArray && seqArray.length > 0) {
                    dispName = String(seqNumber);
                }
                if (seqArray.length > 0) {
                    // Get parent event type input description
                    const dName = getEventTypeInputDisplayStr(
                        eventArray,
                        eventType,
                        seqArray[0].eventTypeGroupSequenceDependencyIdentity,
                        dropdownValuesExist,
                        false,
                        seqCounter,
                        node
                    );
                    if (dName) {
                        dispName = dName;
                    }
                }
            }
            dispName = dispName ? '(' + dispName + ')' : null;
        } else {
            // Check if parent exist, and is concurrent
            if (hasConcurrentParentInCompleteTree(eventType, eventTypeData, node)) {
                dispName = getEventTypeDisplayNameForConcurrent(seqArray, eventType, event, eventArray, seqNumber, node);
            } else {
                if (evTypeGroupInput && evTypeGroupInput.generateConcurrentId) {
                    dispName = '(' + String(seqNumber) + ')';
                }
            }
        }
        return dispName;
    };

    const updateDependencyDisplayName = (
        eventArray: TankersEvent[],
        seqArray: TankersEvent[],
        event: TankersEvent,
        eventType: EventType | null,
        dropdownValuesExist: boolean,
        seqCounter: number,
        eventTypeData: EventTypeData,
        node: IRotationNode
    ): string | null => {
        const evTypeGroupInput: EventTypeGroup | null = eventType ? eventType.eventTypeGroup : null;
        let seqNumber: number | null = 0;
        if (seqArray && seqArray.length > 0) {
            const ind = seqArray.findIndex((o) => o.eventTypeId === event.eventTypeId);
            if (ind > -1) {
                seqArray[ind] = event;
            }
            seqNumber = seqArray[0].concurrentSequenceNumber;
        } else {
            seqNumber = seqCounter;
        }

        let dispName: string | null = null;
        if (eventType && eventType.concurrentEventTypeInputId) {
            dispName = getEventTypeDisplayName(seqArray, eventType, dropdownValuesExist);
            if (!dispName) {
                if (evTypeGroupInput && evTypeGroupInput.generateConcurrentId && !dropdownValuesExist && seqArray && seqArray.length > 0) {
                    dispName = String(seqNumber);
                }
                if (seqArray.length > 0) {
                    // Get parent event type input description
                    const dName = getEventTypeInputDisplayStr(
                        eventArray,
                        eventType,
                        seqArray[0].eventTypeGroupSequenceDependencyIdentity,
                        dropdownValuesExist,
                        false,
                        seqCounter,
                        node
                    );
                    if (dName) {
                        dispName = dName;
                    }
                }
            }
            dispName = dispName ? '(' + dispName + ')' : null;
        } else {
            // Check if parent exist, and is concurrent
            if (hasConcurrentParentInCompleteTree(eventType, eventTypeData, node)) {
                dispName = getEventTypeDisplayNameForConcurrent(seqArray, eventType, event, eventArray, seqNumber, node);
            } else {
                if (evTypeGroupInput && evTypeGroupInput.generateConcurrentId) {
                    dispName = '(' + String(seqNumber) + ')';
                }
            }
        }
        return dispName;
    };

    const generateEventNameWithShortName = (eventType: EventType, seqNumber: number | null): string => {
        if (seqNumber) {
            return '(' + eventType.shortName + ' ' + String(seqNumber) + ')';
        }
        return '(' + eventType.shortName + ')';
    };

    // Generates the 'bracket' description, for multiple event types, either from input value or concurrent sequence number, might return 'null'
    const getEventTypeInputDisplayStr = (
        eventArray: TankersEvent[],
        eventType: EventType | null,
        grSeqDepId: string | null,
        dropdownValuesExist: boolean,
        hasConcurrentParent: boolean,
        seqCounter: number | null,
        node: IRotationNode
    ): string | null => {
        let dispStr: string | null = null;
        const evArray = eventArray.filter((ev) => ev.eventTypeGroupSequenceIdentity === grSeqDepId);
        if (
            (evArray &&
                evArray.length > 0 &&
                evArray[0].eventType &&
                evArray[0].eventType.eventTypeGroup &&
                isMultiple(evArray[0].eventType.eventTypeGroup, node.firstTimeInPortCall)) ||
            hasConcurrentParent
        ) {
            let dispStrTmp = getEventTypeDisplayName(evArray, eventType, dropdownValuesExist);
            if (!dispStrTmp && eventType && eventType.eventTypeGroup && eventType.eventTypeGroup.generateConcurrentId && !dropdownValuesExist) {
                if (hasConcurrentParent) {
                    dispStrTmp = generateEventNameWithShortName(eventType, seqCounter);
                } else {
                    dispStrTmp = String(seqCounter);
                }
            }
            if (dispStrTmp) {
                dispStr = dispStrTmp;
            }
        }
        return dispStr;
    };

    // Generates the display name for event type, for toolbox and event list
    // Uses the first event in the sequence
    // Display name is generated from input values
    const getEventTypeDisplayName = (evList: TankersEvent[], evType: EventType | null, dropdownValuesExist: boolean): string | null => {
        let displayName: string | null = null;
        const sortedEvList = sortOrderedEvents(evList);
        if (sortedEvList && sortedEvList.length > 0 && sortedEvList[0].eventTypeInputRegList && evType && evType.concurrentEventTypeInputId) {
            let inpItem = sortedEvList[0].eventTypeInputRegList.find((inpReg) => inpReg.eventTypeInputId === evType.concurrentEventTypeInputId);
            if (!inpItem) {
                sortedEvList.map((evItem) => {
                    if (!inpItem && evItem.eventTypeInputRegList) {
                        inpItem = evItem.eventTypeInputRegList.find((inpReg) => inpReg.eventTypeInputId === evType.concurrentEventTypeInputId);
                    }
                });
            }

            if (inpItem) {
                switch (inpItem.inputDataTypeId) {
                    case InputDataTypeEnum.Dropdown:
                        if (!dropdownValuesExist && inpItem.dropdownGroupReg) {
                            let displayStr = '';
                            let first = true;
                            inpItem.dropdownGroupReg.dropdownItemRegList.map((dropdownRegItem) => {
                                if (!first) {
                                    displayStr += '/' + dropdownRegItem.dropdownItemValue;
                                } else {
                                    displayStr += dropdownRegItem.dropdownItemValue;
                                    first = false;
                                }
                            });
                            displayName = displayStr;
                        }
                        break;
                    case InputDataTypeEnum.String:
                        displayName = inpItem.stringValueReg;
                        break;
                    case InputDataTypeEnum.Int:
                        displayName = String(inpItem.intValueReg);
                        break;
                    case InputDataTypeEnum.Decimal:
                        displayName = String(inpItem.decimalValueReg);
                        break;
                    case InputDataTypeEnum.Rating:
                        break;
                    case InputDataTypeEnum.Datetime:
                        displayName = String(inpItem.datetimeValueReg);
                        break;
                }
            }
        }
        return displayName;
    };

    const doesDropdownValueExist = (event: TankersEvent): boolean => {
        let dropdownValuesExist = false;
        if (event.eventType && event.eventType.eventTypeInputArray) {
            event.eventType.eventTypeInputArray.map((eventTypeInputItem) => {
                if (event.eventTypeInputRegList) {
                    const evTypeInputReg = event.eventTypeInputRegList.find((eigra) => {
                        return eigra.eventTypeInputId === eventTypeInputItem.eventTypeInputId && eigra.inputDataTypeId === InputDataTypeEnum.Dropdown;
                    });
                    if (evTypeInputReg && evTypeInputReg.dropdownGroupReg) {
                        // If dropdown values exist for the event, do not display these in brackets (because these are displayed later on the row)
                        dropdownValuesExist =
                            evTypeInputReg.dropdownGroupReg.dropdownItemRegList && evTypeInputReg.dropdownGroupReg.dropdownItemRegList.length > 0;
                    }
                }
            });
        }
        return dropdownValuesExist;
    };

    // Called from create-delay-events and confirm new event
    const getDependencyDisplayName = (
        eventArray: TankersEvent[],
        event: TankersEvent,
        eventTypeData: EventTypeData,
        isNewEvent: boolean,
        node: IRotationNode
    ): string | null => {
        let dispName: string | null = null;
        const evArray = [...eventArray];
        const seqArray = evArray.filter((ev) => ev.eventTypeGroupSequenceIdentity === event.eventTypeGroupSequenceIdentity);
        let ev2 = event;
        if (seqArray && seqArray.length > 0) {
            ev2 = seqArray[0];
        }
        if (isNewEvent) {
            evArray.push(event);
            seqArray.push(event);
            dispName = generateDependencyDisplayName(evArray, seqArray, ev2, event.eventType, doesDropdownValueExist(event), 1, eventTypeData, node);
        } else {
            dispName = updateDependencyDisplayName(evArray, seqArray, event, event.eventType, doesDropdownValueExist(event), 1, eventTypeData, node);
        }
        return dispName;
    };

    // Sort ordered event type, by starting at the last, which has NextEventTypeInGroupId = null, and go backwards until the first
    // Returns the array starting with the first element
    const sortOrderedEventTypes = (evTypeArrayInput: EventType[]): EventType[] => {
        const evTypeArray: EventType[] = [];
        let exit = false;
        const arrLen: number = evTypeArrayInput.length;
        let arrCnt = 0;
        let nextId: number;

        // Find last event type group
        const lastEventTypeGroup = evTypeArrayInput.find((ev) => {
            return !ev.nextEventTypeInGroupId;
        });
        if (lastEventTypeGroup) {
            evTypeArray.push(lastEventTypeGroup);
            nextId = lastEventTypeGroup.eventTypeId;
            while (arrCnt < arrLen - 1 && !exit) {
                const previousEventTypeGroup = evTypeArrayInput.find((ev) => {
                    return ev.nextEventTypeInGroupId === nextId;
                });
                if (previousEventTypeGroup) {
                    evTypeArray.unshift(previousEventTypeGroup);
                    nextId = previousEventTypeGroup.eventTypeId;
                } else {
                    exit = true;
                }
                arrCnt++;
            }
        }
        return evTypeArray;
    };

    return {
        getVoyageNameOrSea,
        generateUuid,
        getMovedDeletedStylingClass,
        getFirstPortCallDate,
        getParentEventsSameSequence,
        isRequiredEventTypeInSameEventTypeGroup,
        hasInputGroups,
        hasNotAfterOk,
        findIndexOfLastEventType,
        sortOrderedEvents,
        hasEventGroupDependency,
        filterDuplicateEvents,
        findMissingEventTypeInSequence,
        isNull,
        entityOk,
        isEventOnSelectedNode,
        getParentEvents,
        findParentEventType,
        hasConcurrentParentInCompleteTree,
        getConcurrentSeqNumberInCompleteTree,
        getParentEvSeqForParentSeqNumber,
        getEventTypeDisplayNameForConcurrent,
        findEventTypeGroupSequenceDependentIdentity,
        getEventInputDescription,
        getDepSeqIdentity,
        generateEventNameWithParentDependency,
        generateEventNameWithDependency,
        generateEventNameWithDependencyAndDate,
        generateNewEventNameWithDependency,
        generateDisplayName,
        getEventTypeName,
        generateEventNameWithParent,
        getConcurrentParent,
        generateDependencyDisplayName,
        updateDependencyDisplayName,
        generateEventNameWithShortName,
        getEventTypeInputDisplayStr,
        getEventTypeDisplayName,
        doesDropdownValueExist,
        getDependencyDisplayName,
        sortOrderedEventTypes,
        createErrMsg,
    };
};
